import { Box } from '@mui/material'
import { Style } from '@react-pdf/types'
import parse, { domToReact, HTMLReactParserOptions } from 'html-react-parser'
import React, { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import rehypeStringify from 'rehype-stringify'
import remarkParse from 'remark-parse'
import remarkRehype from 'remark-rehype'
import { unified } from 'unified'
import { containsMarkdown, isFalsy } from 'utilsTS'
import { ImprovedHtml } from '../common-components/ImprovedHtml'

export const parseLabel = (
  label: string | ReactNode,
  returnPdf: boolean = false,
  collapse: boolean = false,
  style?: Style,
  stylesheet?: Record<string, Style>
): ReactNode => {
  if (isFalsy(label)) return ''

  if (typeof label !== 'string') {
    return label as ReactNode
  }

  const replaceNode: HTMLReactParserOptions['replace'] = (domNode: any) => {
    if (domNode.type === 'tag' && domNode.name === 'a') {
      const href = domNode.attribs?.href as string
      if (href?.startsWith('/')) {
        return <Link to={href}>{domToReact(domNode.children)}</Link>
      }
      return (
        <a {...domNode.attribs} target='_blank' rel='noopener noreferrer'>
          {domToReact(domNode.children)}
        </a>
      )
    }
  }

  const containsHTML = /<[^>]+>/i.test(label)
  const isMarkdown = containsMarkdown(label)

  if (!containsHTML && !isMarkdown) {
    return label
  }

  if (containsHTML) {
    if (returnPdf) {
      return (
        <ImprovedHtml style={style} stylesheet={stylesheet} collapse={collapse}>
          {label}
        </ImprovedHtml>
      )
    }

    return (
      <span className='form-parsed-text'>
        {parse(label, { replace: replaceNode })}
      </span>
    )
  }

  const processedLabel = unified()
    .use(remarkParse)
    .use(remarkRehype, { allowDangerousHtml: true })
    .use(rehypeStringify, { allowDangerousHtml: true })
    .processSync(label)
    .toString()

  if (returnPdf) {
    return (
      <ImprovedHtml style={style} stylesheet={stylesheet} collapse={collapse}>
        {processedLabel}
      </ImprovedHtml>
    )
  }

  return (
    <Box
      sx={{
        '& a': {
          color: 'secondary.green',
          textDecoration: 'underline'
        },
        '& a:hover': {
          color: 'primary.main'
        }
      }}
    >{parse(processedLabel, { replace: replaceNode })}
    </Box>
  )
}

export const extractPlainText = (label: string | ReactNode): string => {
  if (!label) return ''
  if (typeof label === 'string') return label
  if (React.isValidElement(label)) {
    if (typeof label.props?.children === 'string') {
      return label.props.children
    }
    if (Array.isArray(label.props?.children)) {
      return label.props.children.join('')
    }
  }
  return String(label)
}
